import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { RouteComponentProps } from "react-router";
import { getFormValues } from "redux-form";

import { SIBLING_COUNTRY_PHONE_PREFIX } from "@/mk2/apps/users/constants";
import { PasswordResetPhone } from "@/mk2/apps/users/containers/PasswordResetPhone/PasswordResetPhone";
import {
    passwordResetPhoneRequestTokenTrigger,
    passwordResetPhoneValidateTokenTrigger,
} from "@/mk2/apps/users/containers/PasswordResetPhone/PasswordResetPhone.actions";
import passwordResetPhoneSaga from "@/mk2/apps/users/containers/PasswordResetPhone/PasswordResetPhone.sagas";
import {
    PASSWORD_RESET_PHONE_FORM_NAME,
    PasswordResetPhoneFormData,
} from "@/mk2/apps/users/containers/PasswordResetPhone/PasswordResetPhoneForm";
import { usersPasswordResetUrl } from "@/mk2/apps/users/urls";
import {
    mkReduxFormMapDispatchToProps,
    mkReduxFormMapStateToProps,
} from "@/mk2/decorators/mkReduxForm";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import FormPage, { FormPageDispatchProps, FormPageStateProps } from "@/mk2/pages/FormPage";
import { AppState } from "@/mk2/reducers";
import { Store } from "@/mk2/store/configureStore";
import { SEO_TITLE_PASSWORD_RESET } from "@/mk/autogenerated/translation";

interface OwnProps {}

export interface StateProps extends FormPageStateProps {
    initialValues: PasswordResetPhoneFormData;
    currentValues: PasswordResetPhoneFormData;
}

export interface DispatchProps extends FormPageDispatchProps {
    onRequestToken(countryPrefix: string, rawPhoneNumber: string, resend: boolean);
    onValidateToken(token: string, fullPhoneNumber: string);
}

export type RouteProps = RouteComponentProps<{}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class PasswordResetPhonePage extends FormPage<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            return {
                ...mkReduxFormMapStateToProps(PASSWORD_RESET_PHONE_FORM_NAME)(state),
                initialValues: {
                    countryPrefix: SIBLING_COUNTRY_PHONE_PREFIX,
                    rawPhoneNumber: "",
                    fullPhoneNumber: "",
                    token: "",
                },
                currentValues: getFormValues(PASSWORD_RESET_PHONE_FORM_NAME)(
                    state,
                ) as PasswordResetPhoneFormData,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            ...mkReduxFormMapDispatchToProps(PASSWORD_RESET_PHONE_FORM_NAME),
            onRequestToken: passwordResetPhoneRequestTokenTrigger,
            onValidateToken: passwordResetPhoneValidateTokenTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        return SEO_TITLE_PASSWORD_RESET;
    }

    public render(props: Props): false | JSX.Element {
        return <PasswordResetPhone {...props} />;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        return usersPasswordResetUrl;
    }

    public getSubmitButton(props: Props): React.ReactElement {
        // disabled because FormPage is used as wizard,
        // not as parent/child hierarchical form
        return null;
    }

    public getConfirmButton(props: Props): React.ReactElement {
        // disabled because FormPage is used as wizard,
        // not as parent/child hierarchical form
        return null;
    }

    public getFooter(props: Props): boolean {
        // disabled because FormPage is used as wizard,
        // not as parent/child hierarchical form
        return false;
    }

    public registerInStore(store: Store<AppState>) {
        store.injectSaga("containers.users.passwordResetPhone", passwordResetPhoneSaga);
    }
}
